import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { CheckCircle2, Sparkles } from "lucide-react";

export default function Register() {
  const [params] = useSearchParams();
  const initialRef = (params.get("ref") || "").toUpperCase();
  const initialRole = params.get("role") === "owner" ? "owner" : "renter";
  const [form, setForm] = useState({ name: "", email: "", password: "", role: initialRole, referral_code: initialRef });
  const [refInfo, setRefInfo] = useState(null);
  const [founder, setFounder] = useState(null);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/founder").then((r) => setFounder(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const code = (form.referral_code || "").trim().toUpperCase();
    if (!code) { setRefInfo(null); return; }
    const t = setTimeout(() => {
      api.get(`/referrals/verify/${encodeURIComponent(code)}`).then((r) => setRefInfo(r.data)).catch(() => setRefInfo({ valid: false }));
    }, 300);
    return () => clearTimeout(t);
  }, [form.referral_code]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.referral_code) delete payload.referral_code;
      await register(payload);
      toast.success("Welcome to SlipShare!");
      navigate(form.role === "owner" ? "/list" : "/search");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (form.referral_code) {
      try {
        localStorage.setItem("slipshare_pending_ref", form.referral_code.trim().toUpperCase());
      } catch (e) {
        console.warn("[Register] Could not persist pending ref:", e?.message);
      }
    }
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="register-page" className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-8">
      <form onSubmit={submit} className="w-full max-w-md">
        <h1 className="font-display text-4xl mb-2">Join SlipShare</h1>
        <p className="text-muted-foreground mb-6">A quieter way to spend a season on the water.</p>

        {founder?.promo_active && form.role === "owner" && (
          <div data-testid="founder-banner" className="mb-6 rounded-xl border border-accent/40 bg-accent/5 px-4 py-3 text-sm flex items-start gap-3">
            <Sparkles className="h-4 w-4 text-accent mt-0.5" strokeWidth={2}/>
            <div>
              <div className="font-medium text-foreground">Founding {founder.spots_total} bonus is live</div>
              <div className="text-muted-foreground mt-0.5">
                Only <strong className="text-accent">{founder.spots_remaining} spots left</strong>. Publish your first dock and we'll credit your account ${founder.bonus_amount.toFixed(0)}.
              </div>
            </div>
          </div>
        )}

        <Button type="button" data-testid="register-google-btn" variant="outline" className="w-full mb-4" onClick={googleLogin}>
          Continue with Google
        </Button>
        <div className="relative my-4 text-center text-xs text-muted-foreground">
          <span className="bg-background px-2 relative z-10">or with email</span>
          <div className="absolute inset-x-0 top-1/2 border-t border-border"/>
        </div>

        <div className="space-y-3">
          <div>
            <Label>I want to</Label>
            <RadioGroup value={form.role} onValueChange={(v) => setForm({...form, role: v})} className="grid grid-cols-2 gap-3 mt-2">
              <label className={`cursor-pointer rounded-lg border ${form.role === "renter" ? "border-primary bg-primary/5" : "border-border"} p-3 flex items-center gap-3`}>
                <RadioGroupItem value="renter" id="renter" data-testid="role-renter-radio"/>
                <div><div className="font-medium text-sm">Rent a slip</div><div className="text-xs text-muted-foreground">Find a dock</div></div>
              </label>
              <label className={`cursor-pointer rounded-lg border ${form.role === "owner" ? "border-primary bg-primary/5" : "border-border"} p-3 flex items-center gap-3`}>
                <RadioGroupItem value="owner" id="owner" data-testid="role-owner-radio"/>
                <div><div className="font-medium text-sm">List my dock</div><div className="text-xs text-muted-foreground">Earn income</div></div>
              </label>
            </RadioGroup>
          </div>
          <div><Label>Name</Label><Input data-testid="register-name-input" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}/></div>
          <div><Label>Email</Label><Input data-testid="register-email-input" type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}/></div>
          <div><Label>Password</Label><Input data-testid="register-password-input" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})}/></div>
          <div>
            <Label>Referral code <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input
              data-testid="register-referral-input"
              value={form.referral_code}
              onChange={(e) => setForm({...form, referral_code: e.target.value.toUpperCase()})}
              placeholder="SLIP-XXXXXX"
              className="uppercase tracking-wider"
            />
            {refInfo?.valid && (
              <div data-testid="referral-valid" className="mt-2 text-xs text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5"/> Referred by {refInfo.referrer_name} — you'll both earn 5% of your first paid booking.
              </div>
            )}
            {form.referral_code && refInfo && !refInfo.valid && (
              <div className="mt-2 text-xs text-muted-foreground">Code not recognized (you can leave this blank).</div>
            )}
          </div>
        </div>
        <Button data-testid="register-submit-btn" type="submit" disabled={loading} className="w-full mt-6 bg-primary hover:bg-primary/90">
          {loading ? "Creating account…" : "Create account"}
        </Button>
        <p className="mt-6 text-sm text-muted-foreground text-center">
          Already have an account? <Link to="/login" data-testid="link-login" className="text-primary link-underline">Sign in</Link>
        </p>
      </form>
    </div>
  );
}
